/**
 *
 * @page 企业中心/数据看板/管理员/项目满意度统计
 *
 **/

import dayjs from 'dayjs';
import { useReducer } from 'react';
import { useRouteMatch, useLocation } from 'react-router-dom';
import { useStoreState } from '&/utils/customHooks';
import { observer } from 'mobx-react';
import BasePageWrap from '&/components/ResourceLib/BasePageWrap';
import { Button, Form, Table, Rate, Spin, Space, message, Tooltip } from 'antd';
import { Select as AntSelect } from '&/components/AntdEncapsulation';
import {
    getManegers,
    getSatisfactionList,
    getSatisfactionDetail,
} from '@/api/personCenter/dataBoard';
import OrganizationSelect from '@/components/UserCenter/OrganizationSelect';
import BaseDrawer from '&/components/BaseDrawer';
import { DAY_OPTIONS, downloadExcel } from './constants';
import './ProjectSatisfactionList.scss';

function ProjectSatisfactionList() {
    const isAdmin = !useRouteMatch({
        path: '/user-center/manager/satisfaction-list',
        strict: false, // 全路径?
        sensitive: true, // 大小写?
    });

    const location = useLocation();
    const { search } = location;
    const urlParams = new URLSearchParams(search);
    const initManager = urlParams.get('manager');
    const initDays = urlParams.get('days');
    const { entId } = useStoreState('dataBoard');

    const [result, setResult] = useState({
        total: 0,
        list: [],
    });
    const [isLoading, setIsLoading] = useState(false);

    const dftParams = {
        page: 1,
        pageNum: 10,
        days: initDays !== null ? +initDays : 30,
        managerId: initManager,
        keyword: null,
        isAll: 1,
        export: 0,
    };

    if (isAdmin) dftParams.entId = entId;

    const [queryForm, setQueryForm] = useState(dftParams);

    const [didUpdate, forceUpdate] = useReducer(s => s + 1, 0);

    // 初始化调用和entId更新调用
    useEffect(() => {
        if (isAdmin) {
            const next = {
                ...queryForm,
                entId,
                isAll: !entId ? 1 : 2,
            };
            setQueryForm(next);
        }
        forceUpdate();
    }, [entId]);

    const onParamChange = (val, key) => {
        setQueryForm(prev => ({ ...prev, [key]: val, page: 1 }));
        key !== 'keyword' && forceUpdate(); // 防止输入时不停触发搜索
    };

    const handlePageChange = (page, pageNum) => {
        setQueryForm(prev => ({ ...prev, page, pageNum }));
        forceUpdate();
    };

    const queryList = async () => {
        const PAYLOAD = { ...queryForm };
        !PAYLOAD.entId && delete PAYLOAD.entId;
        const { export: isExport } = PAYLOAD;
        try {
            setIsLoading(true);
            const res = await getSatisfactionList(PAYLOAD);
            isExport
                ? downloadExcel(
                      res,
                      `项目满意度统计—${
                          managerOpts.find(e => e.value === PAYLOAD.managerId)
                              .label
                      }—${
                          DAY_OPTIONS(intl).find(e => e.value === PAYLOAD.days)
                              .label
                      }—${dayjs().format('YYYY.MM.DD')}`
                  ).catch(err => {
                      if (err.code === 0) {
                          message.warn('无内容');
                      } else {
                          message.warn(err?.msg);
                      }
                  })
                : setResult({
                      list: res.data.list,
                      total: res.data.page?.totalList,
                  });
        } finally {
            setIsLoading(false);
            isExport && setQueryForm(prev => ({ ...prev, export: 0 }));
        }
    };

    useEffect(() => {
        didUpdate > 0 && queryList();
    }, [didUpdate]);

    useEffect(() => {
        queryManagerOptions();
    }, []);

    const dftManeger = {
        value: null,
        label: intl.get('base.all'),
    };
    const [managerOpts, setManagerOpts] = useState([dftManeger]);

    function queryManagerOptions() {
        const PARAMS = {
            // isAll: baseParams.isAll,
            roleId: 2, // 项目经理
        };
        // if (baseParams.entId) {
        //     PARAMS.entId = baseParams.entId;
        // }

        getManegers(PARAMS).then(({ data }) => {
            const opts = [dftManeger];
            for (const el of data.list) {
                opts.push({
                    value: el.userId,
                    label: el.username,
                });
            }
            setManagerOpts(opts);
        });
    }

    const [visible, setVisible] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailData, setDetailData] = useState({
        projectOpinion: {},
        stageOpinion: [],
    });
    const showDetail = ({ projectId }) => {
        setVisible(true);
        setDetailLoading(true);
        getSatisfactionDetail({
            projectId,
        })
            .then(({ data }) => {
                setDetailData(data);
            })
            .finally(() => {
                setDetailLoading(false);
            });
    };

    const onClose = () => {
        setVisible(false);
    };

    const { Item } = Form;
    const { projectOpinion = {} } = detailData;

    const subColumns = [
        {
            title: intl.get('base.serialNum'),
            key: '1',
            width: 70,
            align: 'center',
            render: (_, __, idx) => {
                return idx + 1 + (queryForm.page - 1) * queryForm.pageNum;
            },
        },
        {
            title: '施工阶段',
            dataIndex: 'stageName',
            align: 'center',
        },
        {
            title: '施工结果满意度',
            dataIndex: 'build',
            align: 'center',
            render: val => `${val}星`,
            sorter: {
                compare: (a, b) => a.build - b.build,
            },
        },
        {
            title: '项目经理满意度',
            dataIndex: 'manager',
            align: 'center',
            render: val => `${val}星`,
            sorter: {
                compare: (a, b) => a.manager - b.manager,
            },
        },
        {
            title: '业主意见反馈',
            dataIndex: 'remark',
            align: 'center',
            ellipsis: true,
            render(val) {
                return (
                    <Tooltip
                        autoAdjustOverflow
                        placement="left"
                        overlayClassName="tooltip-container"
                        title={val}>
                        <span>{val}</span>
                    </Tooltip>
                );
            },
        },
        {
            title: '提交时间',
            dataIndex: 'updateDate',
            align: 'center',
            render: date =>
                date ? dayjs.unix(date).format('YYYY.MM.DD HH:mm') : '',
        },
    ];

    const detailModalEl = (
        <BaseDrawer
            visible={visible}
            title="项目满意度详情"
            destroyOnClose
            bodyStyle={{
                padding: '1.375rem 1.25rem',
            }}
            className="satisfaction-detail"
            onClose={onClose}>
            <Spin spinning={detailLoading}>
                <header className="detail__header">
                    {projectOpinion.name}
                </header>

                <div className="score">
                    <section className="score-item">
                        设计阶段
                        <Rate
                            disabled
                            value={projectOpinion.design}
                            count={5}
                        />
                    </section>
                    <section className="score-item">
                        施工结果
                        <Rate disabled value={projectOpinion.build} />
                    </section>
                    <section className="score-item">
                        项目经理
                        <Rate disabled value={projectOpinion.manager} />
                    </section>
                </div>
                <Space size="middle">
                    <Item label="项目经理" className="item-label">
                        <span title={projectOpinion.managerName}>
                            {projectOpinion.managerName}
                        </span>
                    </Item>
                    <Item label="设计师" className="item-label">
                        <span title={projectOpinion.designerName}>
                            {projectOpinion.designerName}
                        </span>
                    </Item>
                </Space>
                <Item label="业主意见">
                    <div className="remark">{projectOpinion.remark}</div>
                </Item>
                <div className="tabel-wrap">
                    <Table
                        className="normal-table"
                        loading={detailLoading}
                        bordered
                        columns={subColumns}
                        dataSource={detailData.stageOpinion}
                        size="small"
                        pagination={false}
                    />
                </div>
            </Spin>
        </BaseDrawer>
    );

    const columns = [
        {
            title: intl.get('base.serialNum'),
            key: '1',
            width: 70,
            align: 'center',
            render: (_, __, idx) => {
                return idx + 1 + (queryForm.page - 1) * queryForm.pageNum;
            },
        },
        {
            title: '项目名称',
            dataIndex: 'name',
            render: (name, row) => {
                return (
                    <span
                        className="native-link"
                        onClick={() => showDetail(row)}>
                        {name}
                    </span>
                );
            },
        },

        {
            title: '项目经理',
            dataIndex: 'managerName',
        },
        {
            title: '设计师',
            align: 'center',
            dataIndex: 'designerName',
        },
        {
            title: '设计阶段满意度',
            align: 'center',
            dataIndex: 'design',
            render: val => `${val}星`,
            sorter: {
                compare: (a, b) => a.design - b.design,
                multiple: 1,
            },
        },
        {
            title: '施工结果满意度',
            align: 'center',
            dataIndex: 'build',
            render: val => `${val}星`,
            sorter: {
                compare: (a, b) => a.build - b.build,
                multiple: 1,
            },
        },
        {
            title: '项目经理满意度',
            align: 'center',
            dataIndex: 'manager',
            render: val => `${val}星`,
            sorter: {
                compare: (a, b) => a.manager - b.manager,
                multiple: 1,
            },
        },
        {
            title: '更新时间',
            dataIndex: 'updateDate',
            align: 'center',
            render: date =>
                date ? dayjs.unix(date).format('YYYY.MM.DD HH:mm') : '',
        },
    ];

    const onExoprt = () => {
        setQueryForm(prev => ({ ...prev, export: 1 }));
        forceUpdate(); // 防止输入时不停触发搜索
    };

    const addBtn = (
        <Button
            type="primary"
            onClick={onExoprt}
            disabled={!result.list.length}>
            导出表单
        </Button>
    );

    return (
        <>
            {isAdmin && <OrganizationSelect />}
            <BasePageWrap
                title={<strong>项目满意度统计</strong>}
                className="project-satisfaction-list"
                type="card"
                headerRight={addBtn}>
                <header className="member-list-form">
                    {isAdmin && (
                        <Item label={'项目经理'}>
                            <AntSelect
                                onChange={e => onParamChange(e, 'managerId')}
                                className="manager-select"
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '')
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                                options={managerOpts}
                                value={queryForm.managerId}
                            />
                        </Item>
                    )}
                    <Item label={'时间区间'}>
                        <AntSelect
                            value={queryForm.days}
                            onChange={e => onParamChange(e, 'days')}
                            options={DAY_OPTIONS(intl)}
                        />
                    </Item>
                </header>

                <Table
                    rowKey="projectId"
                    className="normal-table"
                    loading={isLoading}
                    bordered
                    columns={columns}
                    dataSource={result.list}
                    size="small"
                    pagination={{
                        size: 'small',
                        current: queryForm.page,
                        onChange: handlePageChange,
                        defaultPageSize: queryForm.pageNum,
                        total: result.total,
                        showSizeChanger: false,
                        style: { marginBottom: 0 },
                        showTotal: total => intl.get('base.total', { total }),
                    }}
                    rowClassName="custom-row"
                />
            </BasePageWrap>
            {detailModalEl}
        </>
    );
}

export default observer(ProjectSatisfactionList);
